import { TransactionResponse } from '@ethersproject/providers'
import { Trade, TokenAmount, CurrencyAmount, MON, Fraction, JSBI, Token } from '@monadex/sdk'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTokenAllowance } from './useTokenAllowance'
import { Field } from '@/state/swap/actions'
import {
  useTransactionAdder,
  useHasPendingApproval,
  useTransactionFinalizer
} from '@/state/transactions/hooks'
import { computeSlippageAdjustedAmounts } from '@/utils/price'
import { calculateGasMargin, useWalletData } from '@/utils'

import { useTokenContract } from './useContracts'
import { V1_ROUTER_ADDRESS } from '@/constants/index'
import { useTokenBalance } from '@/state/wallet/hooks'
import { useSwapState } from '@/state/swap/hooks'
export enum ApprovalState {
  UNKNOWN,
  NOT_APPROVED,
  PENDING,
  APPROVED
}

// Returns a state indicating the approval status and a function which approves if necessary
export function useApproveCallback (
  amountToApprove?: CurrencyAmount,
  spender?: string,
  liqPercentage?: number
): [ApprovalState, () => Promise<void>] {
  const { account } = useWalletData()
  const nativeCurrency = MON
  const token =
    amountToApprove instanceof TokenAmount ? amountToApprove.token : undefined

  const currentAllowance = useTokenAllowance(
    token,
    account ?? undefined,
    spender,
    liqPercentage
  )

  const pendingApproval = useHasPendingApproval(token?.address, spender)
  const tokenBalance = useTokenBalance(account, token)

  // Check approval status
  const approvalState: ApprovalState = useMemo(() => {
    if ((amountToApprove == null) || !spender) return ApprovalState.UNKNOWN
    if (amountToApprove.currency === nativeCurrency) return ApprovalState.APPROVED
    if (currentAllowance == null) return ApprovalState.UNKNOWN
    return currentAllowance.lessThan(amountToApprove)
      ? pendingApproval
        ? ApprovalState.PENDING
        : ApprovalState.NOT_APPROVED
      : ApprovalState.APPROVED
  }, [
    amountToApprove,
    currentAllowance,
    nativeCurrency,
    pendingApproval,
    spender
  ])

  const tokenContract = useTokenContract(token?.address)
  const addTransaction = useTransactionAdder()
  const finalizeTransaction = useTransactionFinalizer()

  const approve = useCallback(async (): Promise<void> => {

    if (approvalState !== ApprovalState.NOT_APPROVED) {
      console.error('approve was called unnecessarily')
      return
    }

    if ((token == null) || (tokenContract == null) || (amountToApprove == null) || !spender || (tokenBalance == null)) {
      console.error('missing required approval parameters')
      return
    }

    // TODO: FIX THIS LATER
    // if (tokenBalance.lessThan(amountToApprove)) {
    //   console.error('token balance too low')
    //   return
    // }

    const approveAmount = amountToApprove.raw.toString()
    // ? parseUnits(raffleAdjustedAmount.toFixed(amountToApprove.currency.decimals), amountToApprove.currency.decimals)
    let useExact = false
    const estimatedGas = await tokenContract.estimateGas
      .approve(spender, approveAmount)
      .catch(async () => {
        // general fallback for tokens who restrict approval amounts
        useExact = true
        return await tokenContract.estimateGas.approve(
          spender,
          amountToApprove.raw.toString()
        )
      })

    return tokenContract
      .approve(
        spender,
        useExact
          ? amountToApprove.raw.toString()
          : approveAmount,
        {
          gasLimit: calculateGasMargin(estimatedGas)
        }
      )
      .then(async (response: TransactionResponse) => {
        addTransaction(response, {
          summary: 'Approve ' + amountToApprove.currency.symbol, // eslint-disable-line
          approval: { tokenAddress: token.address, spender: spender as string }, // eslint-disable-line
        })
        try {
          const receipt = await response.wait()
          finalizeTransaction(receipt)
        } catch (e) {
          console.error('Failed to approve token', e)
          throw e
        }
      })
      .catch((error: Error) => {
        console.error('Failed to approve token', error)
        throw error
      })
  }, [
    approvalState,
    token,
    tokenContract,
    amountToApprove,
    spender,
    addTransaction,
    tokenBalance
  ])
  return [approvalState, approve]
}

export function useApproveCallbackFromTrade (
  trade?: Trade,
  allowedSlippage = 0,
  raffleField?: Field
): [ApprovalState, () => Promise<void>, ApprovalState, ApprovalState] {
  const { chainId } = useWalletData()
  const { enterRaffle, fractionOfSwapAmount } = useSwapState()
  const [isApprovingInputToken, setIsApprovingInputToken] = useState(true)

  const inputCurrency = computeSlippageAdjustedAmounts(trade, allowedSlippage)[Field.INPUT]
  const raffleAmountToApprove = useMemo(() => {
    if (trade == null || !raffleField && !enterRaffle) return undefined
    // Get the swap amount adjusted for slippage
    const slippageAdjustedAmount = computeSlippageAdjustedAmounts(trade, allowedSlippage)[raffleField as Field]

    if ((slippageAdjustedAmount == null) || !enterRaffle || !fractionOfSwapAmount) {
      return slippageAdjustedAmount
    }

    // Calculate raffle portion - using same token as input for TokenAmount calculation
    const inputToken = slippageAdjustedAmount.currency as Token
    const inputRaw = slippageAdjustedAmount.raw
    // Convert fraction to basis points (assuming fractionOfSwapAmount is 0-100)
    const raffleRaw = JSBI.divide(
      JSBI.multiply(inputRaw, JSBI.BigInt(fractionOfSwapAmount)),
      JSBI.BigInt(100)
    )
    // Create TokenAmount for raffle portion using same token as input
    const raffleAmount = new TokenAmount(inputToken, raffleRaw)
    // Total amount to approve: swap amount + raffle amount
    return slippageAdjustedAmount.add(raffleAmount)
  }, [trade, allowedSlippage, enterRaffle, fractionOfSwapAmount, raffleField])
  // inputAmountToApprove
  // Get raffle token amount that needs approval (only if raffles enabled and a raffle field is specified)
  // WHEN RAFFLES IS ENABLED APPROVE SWAPPED AMOUNT + % SENT TO RAFFLES
  const inputAmountToApprove = useMemo(
    () => ((trade != null))
      ? computeSlippageAdjustedAmounts(trade, allowedSlippage)[Field.INPUT]
      : undefined,
    [trade, allowedSlippage, enterRaffle, fractionOfSwapAmount]
  )

  // Check if raffle token is native currency (doesn't need approval)
  const isRaffleTokenNative = useMemo(
    () => raffleAmountToApprove?.currency === MON,
    [raffleAmountToApprove]
  )

  // Setup regular input token approval
  const [inputApprovalState, approveInputCallback] = useApproveCallback(
    inputAmountToApprove,
    chainId ? V1_ROUTER_ADDRESS[chainId] : undefined
  )

  // Setup raffle token approval
  const [raffleApprovalState, approveRaffleCallback] = useApproveCallback(
    raffleAmountToApprove,
    chainId ? V1_ROUTER_ADDRESS[chainId] : undefined
  )
  // Determine overall approval state
  // if input is native skip the approval of the inputApprovalState
  const isNative = inputCurrency?.currency.symbol === 'MON'
  const combinedApprovalState = useMemo(() => {
    // If raffles not enabled, just use input approval state
    if (!enterRaffle || !raffleField) {
      return inputApprovalState
    }

    // If input token needs approval, start with that
    if (isApprovingInputToken) {
      return inputApprovalState
    }

    // After input is approved, check raffle token approval
    return raffleApprovalState
  }, [enterRaffle, raffleField, inputApprovalState, raffleApprovalState, isApprovingInputToken, isRaffleTokenNative])

  // Combined approval function
  const combinedApproveCallback = useCallback(async (): Promise<void> => {
    // Standard flow - no raffle
    if (!enterRaffle || !raffleField || isRaffleTokenNative) {
      return await approveInputCallback()
    }
    // Sequential approval flow for raffles
    if (isApprovingInputToken) {
    // First approve input token
      if (inputApprovalState === ApprovalState.NOT_APPROVED) {
        await approveInputCallback()
        setIsApprovingInputToken(false)
        return
      }

      // Input token already approved, move to raffle token
      setIsApprovingInputToken(false)
    }
    // Now approve raffle token
    if (raffleApprovalState === ApprovalState.NOT_APPROVED) {
      return await approveRaffleCallback()
    }
  }, [
    enterRaffle,
    raffleField,
    isRaffleTokenNative,
    inputApprovalState,
    raffleApprovalState,
    approveInputCallback,
    approveRaffleCallback
  ])

  return [combinedApprovalState, combinedApproveCallback, raffleApprovalState, inputApprovalState]
}